import { useMemo, useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';

import Dropdown from '../../ui-components/Dropdown';
import { formatTitle } from '../../utils/common';

import {
  useGetParentMenuAutoCompleteQuery,
  useGetPermissionListQuery,
} from './dynamicMenuApi';

import {
  MENU_OPEN_OPTIONS,
  SEQUENCE_OPTIONS,
  STATUS_OPTIONS,
  type IAllMenu,
  type IMenuForm,
  type IParentMenu,
  type RedirectionOpt,
  type ManageMenuProps,
} from './model';

export const ManageMenu: React.FC<ManageMenuProps> = ({
  onSave,
  initialData,
  onCancel,
}) => {
  const isEdit = Boolean(initialData);

  const { data: parentMenusResp } = useGetParentMenuAutoCompleteQuery();
  const { data: capabilitiesResp } = useGetPermissionListQuery();

  const BORDER_DEFAULT = 'border-gray-300';
  const BORDER_ERROR = 'border-error-500';

  const parentMenus: IParentMenu[] = Array.isArray(parentMenusResp?.data)
    ? parentMenusResp.data
    : [];

  const capabilities = useMemo(() => {
    if (!capabilitiesResp?.data?.records) return [];
    return capabilitiesResp.data.records.map((g: any) => ({
      name: g.group,
      code: g.groupCode,
      permissions: g.permissions.map((p: any) => ({
        name: p.permissionName,
        code: p.permissionCode,
      })),
    }));
  }, [capabilitiesResp]);

  const defaults = (data?: IAllMenu | null): IMenuForm =>
    data
      ? {
          ...data,
          menuType: data.parentId && data.parentId !== 0 ? 'child' : 'parent',
          allowRedirection: data.redirectionAllow as RedirectionOpt,
          url: data.url ?? '',
          icon: data.icon ?? 'home',
        }
      : {
          adminMenuId: 0,
          parentId: null,
          menuDisplay: '',
          icon: '',
          redirectionAllow: 'no',
          url: '',
          open: null,
          capabilityCode: null,
          sequenceNumber: null,
          status: 'Active',
          subMenu: [],
          menuType: 'parent',
          allowRedirection: 'no',
        };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
    control,
  } = useForm<IMenuForm>({ defaultValues: defaults(initialData) });

  useEffect(() => reset(defaults(initialData)), [initialData, reset]);

  const menuType = watch('menuType');
  const allowRedirection = watch('allowRedirection');

  useEffect(() => {
    if (menuType === 'child' && allowRedirection !== 'no') {
      setValue('allowRedirection', 'no', { shouldValidate: true });
    }
  }, [menuType, allowRedirection, setValue]);

  const onSubmit = (data: IMenuForm) => {
    const mapped: IAllMenu = {
      adminMenuId: data.adminMenuId,
      parentId: data.menuType === 'child' ? data.parentId : 0,
      menuDisplay: data.menuDisplay,
      icon: data.icon,
      url:
        data.menuType === 'child' || data.allowRedirection === 'yes'
          ? data.url
          : null,
      open: data.open,
      capabilityCode:
        data.menuType === 'child' || data.allowRedirection === 'yes'
          ? data.capabilityCode
          : null,
      sequenceNumber: data.sequenceNumber,
      status: data.status,
      redirectionAllow: data.allowRedirection,
      subMenu: data.subMenu ?? [],
    };
    onSave(mapped);
    if (!isEdit) reset();
  };

  const handleReset = () => {
    reset(defaults());
    onCancel?.();
  };

  const hasSubMenus = (initialData?.subMenu?.length ?? 0) > 0;
  const showAllowRedir = menuType === 'parent' && (!isEdit || !hasSubMenus);
  const showParentSel = menuType === 'child';
  const showCapAndUrl = menuType === 'child' || allowRedirection === 'yes';
  const showMenuOpen =
    (menuType === 'parent' && allowRedirection === 'yes') ||
    menuType === 'child';

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="max-w-md mx-auto space-y-6"
    >
      <div className="flex items-center justify-between space-x-4">
        <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
          Menu Type <span className="text-error-500">*</span>
        </span>
        <div className="flex space-x-6">
          {['parent', 'child'].map((choice) => (
            <label
              key={choice}
              className={`inline-flex items-center ${
                isEdit ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <input
                type="radio"
                value={choice}
                checked={menuType === choice}
                onChange={() => {
                  if (!isEdit) setValue('menuType', choice);
                }}
                disabled={isEdit}
                className="form-radio h-4 w-4 text-brand-600"
              />
              <span className="ml-2 capitalize">{choice}</span>
            </label>
          ))}
        </div>
      </div>

      {showAllowRedir && (
        <div className="flex items-center justify-between space-x-4">
          <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Redirection Allow <span className="text-error-500">*</span>
          </span>
          <div className="flex space-x-6">
            {['yes', 'no'].map((choice) => (
              <label key={choice} className="inline-flex items-center">
                <input
                  type="radio"
                  value={choice}
                  checked={allowRedirection === choice}
                  onChange={() => setValue('allowRedirection', choice)}
                  className="form-radio h-4 w-4 text-brand-600"
                />
                <span className="ml-2 capitalize">{choice}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {showParentSel && (
        <div className="my-4">
          <Controller
            control={control}
            name="parentId"
            rules={{ required: 'Parent menu is required' }}
            render={({ field }) => (
              <Dropdown
                label="Parent Menu"
                value={String(field.value ?? '')}
                setValue={(val) =>
                  field.onChange(val === '' ? '' : Number(val))
                }
                options={parentMenus.map((menu) => ({
                  label: formatTitle(menu.menuDisplay),
                  value: String(menu.adminMenuId),
                }))}
                placeholder="Parent Menu Select"
                isMulti={false}
                required
                labelKey="label"
                valueKey="value"
                isFocusAllowed={true}
              />
            )}
          />
          {errors.parentId && (
            <p className="error-msg ml-2 mt-[-15px]">
              {String(errors.parentId.message)}
            </p>
          )}
        </div>
      )}

      {showCapAndUrl && (
        <>
          <div className="my-4">
            <Controller
              control={control}
              name="capabilityCode"
              rules={{ required: 'Capability is required' }}
              render={({ field }) => (
                <Dropdown
                  label="Capability"
                  value={String(field.value ?? '')}
                  setValue={field.onChange}
                  options={capabilities.flatMap((group) => [
                    {
                      label: `${group.name}`,
                      value: `__header__${group.code}`,
                      isDisabled: true,
                    },
                    ...group.permissions.map((p) => ({
                      label: `\u00A0— ${p.name}`,
                      value: String(p.code),
                    })),
                  ])}
                  placeholder="Capability Select"
                  labelKey="label"
                  valueKey="value"
                  isMulti={false}
                  required
                  isCreatable={false}
                  isSearchable
                  isFocusAllowed
                />
              )}
            />
            {errors.capabilityCode && (
              <p className="error-msg ml-2 mt-[-15px]">
                {String(errors.capabilityCode.message)}
              </p>
            )}
          </div>

          <div>
            <label className="label">
              URL <span className="required">*</span>
            </label>
            <input
              type="text"
              placeholder="URL Placeholder"
              {...register('url', {
                required: 'URL is required',
                pattern: { value: /^\/.*/, message: 'URL must start with /' },
              })}
              className={`input ${errors.url ? BORDER_ERROR : BORDER_DEFAULT}`}
            />
            {errors.url && (
              <p className="error-msg ml-2 mt-1">
                {String(errors.url.message)}
              </p>
            )}
          </div>
        </>
      )}

      <div>
        <label className="label">
          Display Name <span className="required">*</span>
        </label>
        <input
          type="text"
          placeholder="Display Name Placeholder"
          {...register('menuDisplay', { required: 'Display name is required' })}
          className={`input ${
            errors.menuDisplay ? BORDER_ERROR : BORDER_DEFAULT
          }`}
          autoFocus
        />
        {errors.menuDisplay && (
          <p className="error-msg mt-1 ml-2">
            {String(errors.menuDisplay.message)}
          </p>
        )}
      </div>

      <div>
        <label className="label">Icon Class</label>
        <input
          type="text"
          placeholder="Icon Class"
          {...register('icon', { required: 'Icon is required' })}
          className={`input ${errors.icon ? BORDER_ERROR : BORDER_DEFAULT}`}
        />
        {errors.icon && (
          <p className="error-msg mt-1 ml-2">{String(errors.icon.message)}</p>
        )}
      </div>

      {showMenuOpen && (
        <div className="my-4">
          <Controller
            control={control}
            name="open"
            rules={{ required: 'Menu open is required' }}
            render={({ field }) => (
              <Dropdown
                label="Menu Open"
                value={String(field.value ?? '')}
                setValue={field.onChange}
                options={MENU_OPEN_OPTIONS}
                placeholder="Menu Open Select"
                labelKey="label"
                valueKey="value"
                isMulti={false}
                isCreatable={false}
                isSearchable={false}
              />
            )}
          />
          {errors.open && (
            <p className="error-msg ml-2 mt-[-15px]">
              {String(errors.open.message)}
            </p>
          )}
        </div>
      )}

      <div className="my-2">
        <Controller
          control={control}
          name="sequenceNumber"
          rules={{ required: 'Sequence is required' }}
          render={({ field }) => (
            <Dropdown
              label="Sequence"
              value={String(field.value ?? '')}
              setValue={(val) => field.onChange(val === '' ? '' : Number(val))}
              options={SEQUENCE_OPTIONS}
              placeholder="Sequence Select"
              labelKey="label"
              valueKey="value"
              required
              isMulti={false}
              isCreatable={false}
              isSearchable={false}
            />
          )}
        />
        {errors.sequenceNumber && (
          <p className="error-msg ml-2 mt-[-15px]">
            {String(errors.sequenceNumber.message)}
          </p>
        )}
      </div>

      <div className="my-4">
        <Controller
          control={control}
          name="status"
          rules={{ required: 'Status is required' }}
          render={({ field }) => (
            <Dropdown
              label="Status"
              value={String(field.value || '')}
              setValue={field.onChange}
              options={STATUS_OPTIONS}
              placeholder="Status Select"
              labelKey="label"
              valueKey="value"
              required
              isMulti={false}
              isCreatable={false}
              isSearchable={false}
            />
          )}
        />
        {errors.status && (
          <p className="error-msg ml-2 mt-[-15px]">
            {String(errors.status.message)}
          </p>
        )}
      </div>

      <div className="flex justify-end pt-4 space-x-3">
        <button type="button" onClick={handleReset} className="btn-secondary">
          {isEdit ? 'Cancel' : 'Reset'}
        </button>
        <button type="submit" className="btn-primary">
          Save
        </button>
      </div>
    </form>
  );
};
